
import {
  Dashboard as DashboardIcon,
  Storage as StorageIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CloudUpload as CloudUploadIcon,
  Rule as RuleIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Calculate as CalculateIcon,
  Science as ScienceIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Compress as CompressIcon,
  Timeline as TimelineIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,

  Agriculture as AgricultureIcon,
  PeopleAlt as PeopleAltIcon,
  Landscape as LandscapeIcon,
  Tune as TuneIcon,
  SyncAlt as SyncAltIcon,
  EventAvailable as EventAvailableIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  ManageAccounts as ManageAccountsIcon,
  SmartToy as SmartToyIcon,
  Description as DescriptionIcon,
  Forum as ForumIcon,
  History as HistoryIcon,
  ScatterPlot as ScatterPlotIcon,
  PostAdd as PostAddIcon,
  ReceiptLong as ReceiptLongIcon,
  Home as HomeIcon,
  UploadFile as UploadFileIcon,
  MoveToInbox as MoveToInboxIcon,
  CloudDownload as CloudDownloadIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { MenuItem } from '../types';
import {
  ROLE_SUPER_ADMIN,
  ROLE_ADMINISTRATOR,
  ROLE_AGRONOMIST,
  ROLE_FINANCE,
  ROLE_CLIENT_MANAGER,
  ROLE_EXTERNAL,
  ROLE_PARTNER,
  ROLE_PARTNER_PRODUCT_MANAGER,
  ROLE_PARTNER_API_KEY_MANAGER,
  ROLE_PARTNER_ADMIN,
  ADMIN_ROLES,
  MENU_ID_PARTNER_PRODUCTS,
  MENU_ID_PARTNER_TEAM,
  MENU_ID_PARTNER_API_KEYS,
} from '../constants/roles';

export const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    description: 'Overview and key metrics',
    path: '/dashboard',
    requiresAuth: true,
  },
  // Free-tier "Customer Home" — the guided landing page for External accounts. Placed FIRST among
  // the External items so it is the top of their sidebar. Visible to ALL verified External accounts;
  // internal staff never see it (persona filter). Fresh zero-grant accounts land here (see the
  // AgentPortalDashboard /portal → /portal/home redirect).
  {
    id: 'customer-home',
    label: 'Home',
    icon: <HomeIcon />,
    description: 'Your free-tier home — submit samples and track your submissions',
    path: '/portal/home',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
  },
  // ---- New External IA (portal monetisation, plan §4 / P12): Home · Samples · Reports · Farms ·
  // Profile. Shown ONLY when `/auth/me` reports `billingEnabled` (plan P15) — `requiresBilling` is
  // resolved in Layout.filterMenuItems; until then the legacy items below stay exactly as they are.
  // Each id is also on the External free-tier floor (AuthContext.canAccessMenu) and in navigation.ts.
  {
    id: 'portal-samples',
    label: 'Samples',
    icon: <ScienceIcon />,
    description: 'Track every sample you have submitted',
    path: '/portal/samples',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    requiresBilling: true,
  },
  {
    id: 'portal-reports',
    label: 'Reports',
    icon: <DescriptionIcon />,
    description: 'Your DRIS reports',
    path: '/portal/reports',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    requiresBilling: true,
  },
  {
    id: 'portal-farms',
    label: 'Farms',
    icon: <AgricultureIcon />,
    description: 'Your farms and fields',
    path: '/portal/farms',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    requiresBilling: true,
  },
  {
    id: 'portal-profile',
    label: 'Profile',
    icon: <PersonIcon />,
    description: 'Your details, billing and security',
    path: '/portal/profile',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    requiresBilling: true,
  },
  // SS External self-service (Phase 4a): the 6th sidebar item under the new billing IA, gated on the
  // global `smartSamplingExternalEnabled` flag (resolved in Layout.filterMenuItems) and desktop-only
  // (excluded from the mobile bottom nav — the map editors need a pointer).
  {
    id: 'portal-smart-sampling',
    label: 'Smart Sampling',
    icon: <ScatterPlotIcon />,
    description: 'Zone your fields and place soil samples',
    path: '/portal/smart-sampling',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    requiresBilling: true,
    requiresSmartSampling: true,
    desktopOnly: true,
  },
  // Route-only registrations (never a sidebar entry): the A1 first-login welcome and the checkout.
  {
    id: 'portal-welcome',
    label: 'Welcome',
    icon: <HomeIcon />,
    description: 'First-login welcome',
    path: '/portal/welcome',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
    requiresBilling: true,
  },
  {
    id: 'portal-buy',
    label: 'Buy analyses',
    icon: <ReceiptLongIcon />,
    description: 'Buy a pack of analyses',
    path: '/portal/buy',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
    requiresBilling: true,
  },
  {
    id: 'portal-buy-return',
    label: 'Buy analyses',
    icon: <ReceiptLongIcon />,
    description: 'Payment confirmation after checkout',
    path: '/portal/buy/return',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
    requiresBilling: true,
  },
  // Route-only registrations that exist in BOTH IAs (on the External floor in AuthContext): the AppBar
  // heading and document title come from this lookup, so without an entry they read "Dashboard".
  {
    id: 'analyse-results',
    label: 'Analyse',
    icon: <ScienceIcon />,
    description: 'Analyse an existing lab report',
    path: '/portal/analyse',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
  },
  {
    id: 'onboarding',
    label: 'Set up your profile',
    icon: <HomeIcon />,
    description: 'First-run profile setup',
    path: '/portal/onboarding',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
  },
  // Free-tier Sample Intake (registration-gated pivot) — the baseline External surface. Visible to
  // ALL verified External accounts; internal staff never see it (persona filter). Submit + history.
  {
    id: 'intake-submit',
    label: 'Submit Samples',
    icon: <PostAddIcon />,
    description: 'Announce a batch of samples and get printable bag-tag references',
    path: '/portal/intake',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    // Hidden from the sidebar: the Home landing's inlined Submit hub (<SubmitPathways/>) is now the
    // single submission entry point, and its "Send samples" card routes here. Route kept reachable.
    hidden: true,
  },
  {
    id: 'my-submissions',
    label: 'My Submissions',
    icon: <ReceiptLongIcon />,
    description: 'Your past submissions, reference numbers and reprintable bag tags',
    path: '/portal/submissions',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    // Replaced by `portal-samples` under the new IA (route redirects there) — plan P12.
    hiddenWhenBilling: true,
  },
  // Free-tier Farmer File Upload — the baseline External surface for uploading lab results,
  // shapefiles, yield archives and Omnia soil workbooks with free-text farmer names (deferred
  // linkage). Visible to ALL verified External accounts (persona filter hides it from staff); the
  // free-tier floor in AuthContext.canAccessMenu keeps the route reachable with no seeded grant.
  {
    id: 'file-upload',
    label: 'Submit Files',
    icon: <UploadFileIcon />,
    description: 'Upload lab results, shapefiles, yield or Omnia soil files for a farmer',
    path: '/portal/files',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    // Hidden from the sidebar: the Home landing's inlined Submit hub (<SubmitPathways/>) is now the
    // single submission entry point, and its "Upload files" card routes here. Route kept reachable.
    hidden: true,
  },
  // My Farmers — External free-tier owned-profile CRUD (farmers → farms → fields). Visible to ALL
  // verified External accounts (persona filter hides it from staff); the free-tier floor in
  // AuthContext.canAccessMenu keeps the route reachable with no seeded grant.
  {
    id: 'my-farmers',
    label: 'My Farmers',
    icon: <AgricultureIcon />,
    description: 'Manage your farmers, farms and fields',
    path: '/portal/farmers',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    // Replaced by `portal-farms` under the new IA (route redirects there) — plan P12.
    hiddenWhenBilling: true,
  },
  // Agent Portal — visible ONLY to the External role, and only once the account has been granted
  // data access (see AuthContext.canAccessMenu / Layout.filterMenuItems, both gated on
  // user.hasDataAccess). Fresh free-tier accounts see only the two intake items above; internal
  // staff never see this group (persona filter).
  {
    id: 'agent-portal',
    label: 'My Reports',
    icon: <DescriptionIcon />,
    description: 'Your approved analyses and reports',
    path: '/portal',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    // Replaced by `portal-reports` under the new IA (/portal then lands on Home) — plan P12.
    hiddenWhenBilling: true,
  },
  // Per-batch workspace under /portal/batches/:batchId. Registered as `hidden` so route handlers
  // (router.tsx) resolve the menu id for selection/full-width handling, without rendering a
  // sidebar entry. Pattern matches WorkspacePage's anonymous handle ({ fullWidth: true }) but lets
  // the AppBar title fall back to this label rather than "Dashboard".
  {
    id: 'agent-portal-workspace',
    label: 'Report Workspace',
    icon: <DescriptionIcon />,
    description: 'Index-free workspace for one approved batch',
    requiresAuth: true,
    roles: [ROLE_EXTERNAL],
    hidden: true,
  },
  // Partner Portal — visible ONLY to the Partner role. Partner users see only this group.
  {
    id: 'partner-portal',
    label: 'Partner Home',
    icon: <DashboardIcon />,
    description: 'Partner integration overview',
    path: '/partner',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    id: 'partner-workflow-new',
    label: 'New Workflow',
    icon: <AssignmentTurnedInIcon />,
    description: 'Upload result files against a selected client, farm and crop',
    path: '/partner/workflow/new',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    id: 'partner-workflows',
    label: 'My Workflows',
    icon: <HistoryIcon />,
    description: 'Your submitted workflows and their status',
    path: '/partner/workflows',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    id: 'partner-entities',
    label: 'My Farmers',
    icon: <PeopleAltIcon />,
    description: 'Your farmers, farms and plots',
    path: '/partner/entities',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    id: 'partner-results',
    label: 'My Results',
    icon: <DescriptionIcon />,
    description: 'Your approved analysis results',
    path: '/partner/results',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    // Per-partner products self-service. Unlike the rest of the partner-* group, this is NOT part of
    // the universal partner portal floor — it is gated to the PartnerProductManager add-on role, so a
    // plain Partner neither sees this menu nor reaches /partner/products (see AuthContext.canAccessMenu
    // and Layout.filterMenuItems, both of which key off these `roles`).
    id: MENU_ID_PARTNER_PRODUCTS,
    label: 'Products',
    icon: <Inventory2Icon />,
    description: 'Manage your products and element conversion factors',
    path: '/partner/products',
    requiresAuth: true,
    roles: [ROLE_PARTNER_PRODUCT_MANAGER],
  },
  {
    // Team management self-service. Like Products, NOT part of the universal partner-* floor — gated
    // to the PartnerAdmin add-on role (see AuthContext.canAccessMenu and Layout.filterMenuItems).
    id: MENU_ID_PARTNER_TEAM,
    label: 'Team',
    icon: <ManageAccountsIcon />,
    description: 'Manage your organisation’s users and their permissions',
    path: '/partner/team',
    requiresAuth: true,
    roles: [ROLE_PARTNER_ADMIN],
  },
  {
    // API Keys is now gated to the PartnerApiKeyManager add-on role rather than the universal
    // partner-* floor, so PAT minting can be granted/revoked per user by a PartnerAdmin.
    id: MENU_ID_PARTNER_API_KEYS,
    label: 'API Keys',
    icon: <VpnKeyIcon />,
    description: 'Generate and manage integration tokens',
    path: '/partner/api-keys',
    requiresAuth: true,
    roles: [ROLE_PARTNER_API_KEY_MANAGER],
  },
  {
    // Consolidated partner Communication page (bookings + messages across the partner's own share
    // links). Part of the universal partner-* floor — no DB seed needed (AuthContext.canAccessMenu
    // grants any partner-* id to the Partner role).
    id: 'partner-communication',
    label: 'Communication',
    icon: <ForumIcon />,
    description: 'Bookings and messages from your share links',
    path: '/partner/communication',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
  },
  {
    id: 'partner-results-workspace',
    label: 'Result Workspace',
    icon: <DescriptionIcon />,
    description: 'Index-free workspace for one approved batch',
    requiresAuth: true,
    roles: [ROLE_PARTNER],
    hidden: true,
  },
  // Capability-only permission for generating/managing public report share links from the internal
  // workspace + Reports tab. NOT a navigable screen: `hidden: true` keeps it out of the sidebar
  // (Sidebar.tsx filters `!hidden`), while the placeholder `path` makes it appear as a togglable row in
  // the admin permission matrix (MenuPermissions.tsx buildMenuRows emits leaves that have a `path`). No
  // route is registered for this path — it never renders a link. Seeded on for SuperAdmin/Administrator/
  // Agronomist (see migration); an admin can revoke it per role, which both hides the button AND 403s the
  // create/manage endpoints server-side (ShareLinkManagementController).
  {
    id: 'share-links',
    label: 'Share Links (generate/manage)',
    icon: <DescriptionIcon />,
    description: 'Generate and manage public report share links',
    path: '/internal/share-links',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_AGRONOMIST],
    hidden: true,
  },
  {
    id: 'workflow-dashboard',
    label: 'Workflow',
    icon: <AssignmentTurnedInIcon />,
    description: 'Manage batch workflows',
    path: '/workflow',
    requiresAuth: true,
    roles: [...ADMIN_ROLES, ROLE_AGRONOMIST, ROLE_FINANCE, ROLE_CLIENT_MANAGER],
  },
  {
    // Consolidated internal Communication page: cross-batch bookings + contact messages. Seeded to
    // the same three roles as share-links (whose action endpoints the page reuses) via
    // SeedCommunicationMenuPermission; admin-revokable in the permission matrix, enforced server-side
    // by the "communication" authorization policy.
    id: 'communication',
    label: 'Communication',
    icon: <ForumIcon />,
    description: 'Bookings and messages from all share links',
    path: '/communication',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_AGRONOMIST],
  },
  {
    // Internal cross-farmer "Uploaded Files" inbox: portal-account file uploads awaiting linkage to a
    // real Farmer. Gated to the same staff triage roles as the sample-intake review surface. Route
    // reachability for SuperAdmin/Administrator is unconditional in canAccessMenu; Agronomist reach
    // additionally requires the menu permission seeded server-side (DB-driven allowedMenuIds).
    //
    // Hidden from the sidebar: the inbox now lives as the Communication page's "Uploaded Files" tab,
    // the single entry point. The object is KEPT so the /uploaded-files route (a redirect to
    // /communication?tab=files) stays reachable and the seeded menu permission stays inert.
    id: 'uploaded-files',
    label: 'Uploaded Files',
    icon: <MoveToInboxIcon />,
    description: 'Farmer file uploads awaiting linkage and hand-off',
    path: '/uploaded-files',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_AGRONOMIST],
    hidden: true,
  },
  {
    // Smart Sampling runs (boundary capture → Omnia import → processing). Gated like the
    // Communication page: role floor here, plus the strict `hasMenuCapability('smart-sampling')`
    // gate in-page for mutating affordances, honoring an admin un-ticking the permission in the
    // matrix (allowedMenuIds). Server-side authorization remains the security boundary.
    id: 'smart-sampling',
    label: 'Smart Sampling',
    icon: <ScatterPlotIcon />,
    description: 'Boundary-driven sampling runs per farm',
    path: '/smart-sampling',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_AGRONOMIST],
  },
  {
    // Fertilizer Program Builder — Phase 1a read-only product master. Gated like Smart Sampling:
    // role floor here, plus the admin-revokable `fertilizer-programs` menu capability (server-side
    // FertilizerProgramsPermissionHandler is the security boundary). The `id` MUST match the backend
    // menu_item_id seeded in role_menu_permissions.
    id: 'fertilizer-programs',
    label: 'Fertilizer',
    icon: <ScienceIcon />,
    description: 'Fertilizer programs, removal bands, product master + adjudication queue',
    path: '/fertilizer-programs',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_AGRONOMIST],
  },
  {
    id: 'batches',
    label: 'Batches',
    icon: <InventoryIcon />,
    description: 'Manage sample batches and groupings',
    path: '/batches',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN],
  },
  {
    id: 'data-import',
    label: 'Data Import',
    icon: <CloudUploadIcon />,
    description: 'Import CSV files from laboratories for DRIS analysis',
    path: '/import',
    requiresAuth: true,
    roles: ADMIN_ROLES,
  },
  {
    id: 'finance-parent',
    label: 'Finance',
    icon: <AccountBalanceIcon />,
    description: 'Financial management and billing',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_FINANCE],
    children: [
      {
        id: 'consolidated-invoicing',
        label: 'Consolidated Invoicing',
        icon: <AccountBalanceIcon />,
        description: 'Manage consolidated billing periods and invoices',
        path: '/finance/consolidated',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR, ROLE_FINANCE],
      },
    ],
  },
  {
    id: 'people-organizations-parent',
    label: 'People & Organizations',
    icon: <PeopleAltIcon />,
    description: 'Manage people, organizations, and relationships',
    requiresAuth: true,
    children: [
      {
        id: 'entity-search',
        label: 'Farm Workspace',
        icon: <AgricultureIcon />,
        description: 'One workspace per farmer: farms, plots, batches, workflows, Smart Sampling and programs',
        path: '/farm-workspace',
        requiresAuth: true,
      },
      {
        id: 'farmers',
        label: 'Farmers',
        icon: <PersonIcon />,
        description: 'Manage farmer records',
        path: '/farmers',
        requiresAuth: true,
      },
      {
        id: 'agents',
        label: 'Agents',
        icon: <PersonIcon />,
        description: 'Manage agent records',
        path: '/agents',
        requiresAuth: true,
      },
      {
        id: 'labs',
        label: 'Labs',
        icon: <ScienceIcon />,
        description: 'Manage laboratory records',
        path: '/labs',
        requiresAuth: true,
      },

    ],
  },
  {
    id: 'land-property-parent',
    label: 'Land & Property',
    icon: <LandscapeIcon />,
    description: 'Manage farms and land parcels',
    requiresAuth: true,
    children: [
      {
        id: 'farms',
        label: 'Farms',
        icon: <AgricultureIcon />,
        description: 'Manage farm records',
        path: '/farms',
        requiresAuth: true,
      },
      {
        id: 'farm-plots',
        label: 'Farm Plots',
        icon: <AgricultureIcon />,
        description: 'Manage farm plot records',
        path: '/farm-plots',
        requiresAuth: true,
      },
    ],
  },
  {
    id: 'science-classification-parent',
    label: 'Science & Classification',
    icon: <ScienceIcon />,
    description: 'Manage scientific classifications and reference data',
    requiresAuth: true,
    children: [
      {
        id: 'crop-types',
        label: 'Crop Types',
        icon: <StorageIcon />,
        description: 'Manage crop type classifications',
        path: '/crop-types',
        requiresAuth: true,
      },
      {
        id: 'crop-sub-types',
        label: 'Crop Sub-Types',
        icon: <StorageIcon />,
        description: 'Manage crop sub-type classifications',
        path: '/crop-sub-types',
        requiresAuth: true,
      },
      {
        id: 'sample-types',
        label: 'Sample Types',
        icon: <StorageIcon />,
        description: 'Manage sample type definitions',
        path: '/sample-types',
        requiresAuth: true,
      },
      {
        id: 'ratio-configurations',
        label: 'Ratio Configurations',
        icon: <StorageIcon />,
        description: 'Manage nutrient ratio configurations',
        path: '/ratio-configurations',
        requiresAuth: true,
      },
      {
        id: 'element-rankings',
        label: 'Element Rankings',
        icon: <RuleIcon />,
        description: 'Manage nutrient element rankings and classifications',
        path: '/element-rankings',
        requiresAuth: true,
      },
    ],
  },
  {
    id: 'norms-parent',
    label: 'Norms',
    icon: <RuleIcon />,
    description: 'Manage DRIS reference norms',
    requiresAuth: true,
    children: [
      {
        id: 'hybrid-norms-view',
        label: 'Hybrid Norms',
        icon: <StorageIcon />,
        description: 'View and manage saved Hybrid DRIS norms',
        path: '/hybrid-norms-view',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'hybrid-norms-calculator',
        label: 'Hybrid Norms Calculator',
        icon: <CalculateIcon />,
        description: 'Generate DRIS norms using Hybrid v7.0 methodology',
        path: '/hybrid-norms',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'nutrient-norms',
        label: 'Nutrient Norms',
        icon: <RuleIcon />,
        description: 'Manage DRIS reference values for sap, soil, and tissue analysis',
        path: '/norms',
        requiresAuth: true,
      },
    ],
  },
  {
    id: 'configuration-parent',
    label: 'Configuration',
    icon: <TuneIcon />,
    description: 'Application settings and configuration',
    requiresAuth: true,
    children: [
      {
        id: 'ai-prompt',
        label: 'AI Prompt',
        icon: <PsychologyIcon />,
        description: 'Configure AI prompt settings',
        path: '/settings/ai-prompt',
        requiresAuth: true,
      },
      {
        id: 'message-templates',
        label: 'Templates',
        icon: <DescriptionIcon />,
        description: 'Manage message & email templates',
        path: '/settings/templates',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
      },
      {
        id: 'compression-training',
        label: 'Compression Training',
        icon: <CompressIcon />,
        description: 'Train quantile compression parameters for DRIS index display',
        path: '/settings/compression-training',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'dris-calculation-settings',
        label: 'DRIS Calculation Settings',
        icon: <ScienceIcon />,
        description: 'Configure HybridDRIS algorithm parameters',
        path: '/settings/dris-calculation',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'element-breakpoints',
        label: 'Element Breakpoints',
        icon: <TimelineIcon />,
        description: 'Manage Graph Index to Element Rate breakpoints',
        path: '/settings/element-breakpoints',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'product-factors',
        label: 'Product Factors',
        icon: <InventoryIcon />,
        description: 'Manage element to product conversion factors',
        path: '/settings/product-factors',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'recalculate-samples',
        label: 'Recalculate Samples',
        icon: <RefreshIcon />,
        description: 'Bulk recalculation of DRIS analysis and recommendations',
        path: '/settings/recalculate-samples',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
    ],
  },
  {
    id: 'integrations-parent',
    label: 'Integrations',
    icon: <SyncAltIcon />,
    description: 'External service integrations',
    requiresAuth: true,
    children: [
      {
        id: 'sage-settings',
        label: 'Sage Settings',
        icon: <AccountBalanceIcon />,
        description: 'Manage Sage accounting reference data',
        path: '/settings/sage',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'sage-sample-type-mappings',
        label: 'Analysis Type Mappings',
        icon: <ScienceIcon />,
        description: 'Link analysis types (Sap, Soil, Tissue) to Sage items',
        path: '/sample-types',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'calendar-settings',
        label: 'Calendar',
        icon: <EventAvailableIcon />,
        description: 'Central Google Calendar mailbox and partner booking calendars',
        path: '/settings/calendar',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
      },
      {
        id: 'claude-settings',
        label: 'Claude AI',
        icon: <SmartToyIcon />,
        description: 'Claude AI configuration and usage',
        path: '/settings/claude',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
    ],
  },
  {
    id: 'administration-parent',
    label: 'Administration',
    icon: <AdminPanelSettingsIcon />,
    description: 'System administration and permissions',
    requiresAuth: true,
    roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
    children: [
      {
        id: 'user-management',
        label: 'User Management',
        icon: <PeopleAltIcon />,
        description: 'Manage users, roles, and permissions',
        path: '/user-management',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'role-management',
        label: 'Role Management',
        icon: <SecurityIcon />,
        description: 'Manage system roles and permissions',
        path: '/admin/roles',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
      },
      {
        id: 'workflow-permissions',
        label: 'Workflow Permissions',
        icon: <VpnKeyIcon />,
        description: 'Configure workflow status permissions by role',
        path: '/admin/workflow-permissions',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'menu-permissions',
        label: 'Menu Permissions',
        icon: <VpnKeyIcon />,
        description: 'Configure menu item visibility by role',
        path: '/admin/menu-permissions',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        id: 'access-grants',
        label: 'Access Grants',
        icon: <VpnKeyIcon />,
        description: 'Grant external users row-level access',
        path: '/admin/access-grants',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
      },
      {
        id: 'report-audit',
        label: 'Report Access Audit',
        icon: <HistoryIcon />,
        description: 'Audit of who viewed shared reports',
        path: '/admin/report-audit',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN, ROLE_ADMINISTRATOR],
      },
      {
        // SuperAdmin bulk data-extraction (flat CSV + AI bundle). SuperAdmin-only both here and at the
        // API (DataExtractionController is [Authorize(Roles = SuperAdmin)]). The `id` MUST match the
        // route handle in router.tsx and the seeded menu_item_id in role_menu_permissions.
        id: 'data-extraction',
        label: 'Data Extraction',
        icon: <CloudDownloadIcon />,
        description: 'Bulk-export raw lab and analysis data as CSV or an AI bundle',
        path: '/settings/data-extraction',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        // SuperAdmin duplicate-DETECTION report (Plan C, Layer 4). Scans for duplicate
        // Farmer/Farm/Plot clusters and hands each off to the merge tool. SuperAdmin-only both here
        // and at the API. The `id` MUST match the route handle in router.tsx and the seeded
        // menu_item_id in role_menu_permissions.
        id: 'duplicate-scan',
        label: 'Duplicate Detection',
        icon: <ContentCopyIcon />,
        description: 'Find and merge duplicate farmers, farms and plots',
        path: '/admin/duplicate-scan',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
      {
        // SuperAdmin billing catalog (D11, §4.3): edit product price/currency/active. SuperAdmin-only both
        // here and at the API. The `id` MUST match the route handle in router.tsx.
        id: 'billing-products',
        label: 'Billing Products',
        icon: <ReceiptLongIcon />,
        description: 'Set product prices, currency and availability',
        path: '/admin/billing-products',
        requiresAuth: true,
        roles: [ROLE_SUPER_ADMIN],
      },
    ],
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: <PersonIcon />,
    description: 'Profile settings and account preferences',
    path: '/profile',
    requiresAuth: true,
  },
  {
    id: 'legacy-calculator',
    label: 'Legacy Calculator',
    icon: <CalculateIcon />,
    description: 'Original DRIS calculations from legacy system',
    path: '/legacy-calculator',
    requiresAuth: true,
    roles: ADMIN_ROLES,
  },
];
